'use client';

import { type SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { modalRegistry } from '../utils/modal-registry';

/**
 * React hook for accessing and manipulating the modal registry.
 * Provides a React-friendly interface to the global modal state.
 *
 * @returns An object containing:
 *   - activeIds: Array of currently active modal IDs
 *   - isActive: Function to check if a specific modal is active
 *   - open: Function to open a modal
 *   - close: Function to close a modal
 */
export const useModalRegistry = () => {
  // Initialize with current state from the registry
  const [activeIds, setActiveIds] = useState<Array<string>>(modalRegistry.getActiveIds());

  // Track modals opened by this hook instance for cleanup on unmount
  const openedByThisHook = useRef<Set<string>>(new Set());

  // Opens a modal with the given ID.
  const open = useCallback((multiModalTriggerId?: string, event?: SyntheticEvent | Event) => {
    if (multiModalTriggerId) {
      openedByThisHook.current.add(multiModalTriggerId);
    }
    modalRegistry.open(multiModalTriggerId, event);
  }, []);

  // Closes a modal with the given ID.
  const close = useCallback((multiModalTriggerId?: string) => {
    if (multiModalTriggerId) {
      openedByThisHook.current.delete(multiModalTriggerId);
    }
    modalRegistry.close(multiModalTriggerId);
  }, []);

  // Checks if a specific modal is currently active.
  const isActive = useCallback(
    (multiModalTriggerId: string) => {
      return activeIds.includes(multiModalTriggerId);
    },
    [activeIds],
  );

  useEffect(() => {
    // Capture the ref value inside the effect for safe cleanup access
    const openedModals = openedByThisHook.current;

    // Subscribe to changes and update state when modals change
    const unsubscribe = modalRegistry.subscribe(() => {
      setActiveIds(modalRegistry.getActiveIds());
    });

    // Clean up subscription when component unmounts
    return () => {
      // Close only modals that were opened by this hook instance
      openedModals.forEach((id) => {
        modalRegistry.close(id);
      });
      openedModals.clear();
      unsubscribe();
    };
  }, []);

  /**
   * Memoized return value to prevent unnecessary re-renders in consuming components.
   */
  return useMemo(
    () => ({
      activeIds,
      isActive,
      open,
      close,
    }),
    [activeIds, isActive, open, close],
  );
};
